import React, { useCallback, useEffect, useState } from "react";
import {
  FaCheckCircle,
  FaChevronDown,
  FaCopy,
  FaExclamationCircle,
  FaFacebook,
  FaGoogle,
  FaInstagram,
  FaLinkedin,
  FaPlug,
  FaPlus,
  FaSave,
  FaSignInAlt,
  FaSync,
  FaTimes,
  FaTrash,
  FaWhatsapp,
  FaYoutube,
} from "react-icons/fa";
import { useConfirm, useNotify } from "../../../../components/ConfirmModal";
import { errorMessage, portalWorkApi, staffWorkApi } from "../../../work/workApi";
import { copyText, fmtShort } from "./setupUi";

// Accounts added with an id + token: required fields per platform.
const TOKEN_PLATFORMS = {
  Facebook: {
    provider: "meta",
    idLabel: "Page ID",
    idHint: "Page > About > Page transparency, or Meta Business Suite > Settings > Pages",
    nameLabel: "Page name",
    tokenLabel: "Page access token",
    tokenHint: "Business settings > System users > Generate token (pages_manage_posts, pages_read_engagement, leads_retrieval)",
  },
  Instagram: {
    provider: "meta",
    idLabel: "Instagram business account ID",
    idHint: "Meta Business Suite > Settings > Instagram accounts",
    nameLabel: "Username",
    tokenLabel: "Access token",
    tokenHint: "Token of the Facebook Page linked to this Instagram account",
  },
  LinkedIn: {
    provider: "linkedin",
    idLabel: "Organization / member ID",
    idHint: "The number in urn:li:organization:12345",
    nameLabel: "Page name",
    tokenLabel: "Access token",
  },
  YouTube: {
    provider: "youtube",
    idLabel: "Channel ID",
    idHint: "Starts with UC… (YouTube Studio > Settings > Channel > Advanced)",
    nameLabel: "Channel name",
    tokenLabel: "Access token",
    refresh: true,
  },
  WhatsApp: {
    provider: "whatsapp",
    idLabel: "Phone Number ID",
    idHint: "WhatsApp Manager > Phone numbers (the ID, not the phone number)",
    wabaLabel: "WhatsApp Business Account ID",
    tokenLabel: "Permanent access token",
    tokenHint: "System user token with whatsapp_business_messaging and whatsapp_business_management",
  },
};

const OPTIONS = [
  { key: "Facebook", label: "Facebook Page", kind: "token" },
  { key: "Instagram", label: "Instagram", kind: "token" },
  { key: "LinkedIn", label: "LinkedIn", kind: "token" },
  { key: "YouTube", label: "YouTube", kind: "token" },
  { key: "WhatsApp", label: "WhatsApp Business", kind: "token" },
  { key: "MetaLeads", label: "Meta lead forms (Facebook / Instagram ads)", kind: "metaLeads" },
  { key: "GoogleLeads", label: "Google Ads lead forms", kind: "googleLeads" },
  { key: "MetaAds", label: "Meta ad account", kind: "signin", provider: "metaads", signInLabel: "Facebook" },
  { key: "GoogleAds", label: "Google Ads account", kind: "signin", provider: "googleads", signInLabel: "Google" },
];

const PLATFORM_ICONS = {
  Facebook: FaFacebook,
  Instagram: FaInstagram,
  LinkedIn: FaLinkedin,
  YouTube: FaYoutube,
  WhatsApp: FaWhatsapp,
  MetaAds: FaFacebook,
  GoogleAds: FaGoogle,
  "Meta lead forms": FaFacebook,
  "Google lead forms": FaGoogle,
};
const PLATFORM_LABELS = { MetaAds: "Meta ads", GoogleAds: "Google Ads" };
const HEALTH = {
  ok: { label: "Working", tone: "verified" },
  expiring: { label: "Sign in again soon", tone: "waiting" },
  expired: { label: "Reconnect needed", tone: "blocked" },
  error: { label: "Check failed", tone: "blocked" },
  receiving: { label: "Receiving leads", tone: "verified" },
  waiting: { label: "Waiting for first lead", tone: "todo" },
};
const EMPTY_FORM = { id: "", name: "", waba: "", token: "", refresh: "", page: "" };

const leadStatusText = (s) =>
  s?.last_lead_on
    ? `Last lead ${fmtShort(s.last_lead_on)} · ${s.recent_leads} in ${s.recent_days} days`
    : "No leads received yet";

function ResultLine({ result }) {
  if (!result) return <span className="dmp-muted">Tokens and keys are stored encrypted and never shown again.</span>;
  return (
    <span className={`dms-cx__result${result.Ok ? " is-ok" : " is-bad"}`}>
      {result.Ok ? <FaCheckCircle aria-hidden="true" /> : <FaExclamationCircle aria-hidden="true" />}
      {result.Text}
    </span>
  );
}

function CopyValue({ label, value }) {
  const notify = useNotify();
  if (!value) return null;
  return (
    <div className="dms-cx__copy">
      <span className="dms-cx__copy-label">{label}</span>
      <code>{value}</code>
      <button
        type="button"
        className="dms-icon-btn dms-cx__btn"
        title={`Copy ${label}`}
        onClick={async () => (await copyText(value)) && notify.success(`${label} copied`)}
      >
        <FaCopy aria-hidden="true" />
      </button>
    </div>
  );
}

/** API connection setup inside the Setup tab: connected accounts table, plus "Add connection" (test, then save). */
function SetupConnections({ isStaff, tenantId, open, collapsible = true, onToggle }) {
  const notify = useNotify();
  const confirm = useConfirm();
  const workApi = isStaff ? staffWorkApi : portalWorkApi;
  const params = isStaff ? { tenant_id: tenantId } : undefined;
  const [rows, setRows] = useState(null);
  const [leads, setLeads] = useState(null);
  const [providers, setProviders] = useState({});
  const [adding, setAdding] = useState(false);
  const [option, setOption] = useState("Facebook");
  const [form, setForm] = useState(EMPTY_FORM);
  const [result, setResult] = useState(null);
  const [freshKey, setFreshKey] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [conn, wa, sources] = await Promise.all([
        workApi.connections(params),
        workApi.waNumbers(params).catch(() => null),
        workApi.leadSources(params).catch(() => null),
      ]);
      setProviders(conn?.providers || {});
      setLeads(sources);
      setRows([
        ...(conn?.connections || []).map((c) => ({
          key: `c${c.Connection_Id}`,
          kind: "social",
          id: c.Connection_Id,
          platform: c.Platform,
          name: c.Account_Name,
          externalId: c.External_Account_Id,
          health: c.Health,
          error: c.Last_Error,
          checkedOn: c.Last_Checked_On,
        })),
        ...(wa?.numbers || []).map((n) => ({
          key: `w${n.Number_Id}`,
          kind: "whatsapp",
          id: n.Number_Id,
          platform: "WhatsApp",
          name: n.Verified_Name || n.Display_Number,
          externalId: n.Display_Number || n.Phone_Number_Id,
          health: n.Health,
          error: n.Last_Error,
          checkedOn: n.Last_Checked_On,
        })),
      ]);
    } catch (err) {
      notify.error(errorMessage(err, "Could not load API connections"));
      setRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workApi, tenantId]);

  const isOpen = open || !collapsible;
  useEffect(() => {
    if (isOpen && rows === null) load();
  }, [isOpen, rows, load]);

  const fbPages = (rows || []).filter((r) => r.platform === "Facebook");
  const google = leads?.google;
  const meta = leads?.meta;
  const leadRows = [
    ...(meta?.pages?.length
      ? [{
          key: "meta-leads",
          kind: "metaLeads",
          platform: "Meta lead forms",
          name: meta.pages.map((p) => p.Account_Name).join(", "),
          externalId: `${meta.pages.length} page${meta.pages.length === 1 ? "" : "s"}`,
          health: meta.last_lead_on ? "receiving" : "waiting",
          sub: leadStatusText(meta),
        }]
      : []),
    ...(google?.has_key || google?.vault_key
      ? [{
          key: "google-leads",
          kind: "googleLeads",
          platform: "Google lead forms",
          name: google.has_key ? `Webhook key ${google.key_hint}` : "Key from Vault (Google Ads app)",
          externalId: "Webhook",
          health: google.last_lead_on ? "receiving" : "waiting",
          sub: leadStatusText(google),
        }]
      : []),
  ];
  const allRows = [...(rows || []), ...leadRows];
  const firstTime = rows !== null && allRows.length === 0;
  const showForm = adding || firstTime;

  const opt = OPTIONS.find((o) => o.key === option);
  const cfg = TOKEN_PLATFORMS[option];
  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setResult(null);
  };
  const pickOption = (value) => {
    setOption(value);
    setForm(EMPTY_FORM);
    setResult(null);
    setFreshKey(null);
  };
  const run = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
    } catch (err) {
      setResult({ Ok: false, Text: errorMessage(err, "Something went wrong") });
    } finally {
      setBusy("");
    }
  };

  // ---- table actions
  const testMetaPages = async (pages, subscribe = false) => {
    const out = [];
    for (const page of pages) {
      // eslint-disable-next-line no-await-in-loop
      out.push({ page, res: await workApi.testMetaLeadForms(page.Connection_Id, { subscribe }) });
    }
    return out;
  };

  const testRow = async (row) => {
    setBusy(row.key);
    try {
      if (row.kind === "metaLeads") {
        const results = await testMetaPages(meta.pages);
        const bad = results.find((r) => !r.res.Ok);
        if (bad) notify.error(`${bad.page.Account_Name}: ${bad.res.Message}`);
        else {
          const forms = results.reduce((n, r) => n + r.res.Forms.length, 0);
          const off = results.filter((r) => !r.res.Lead_Delivery).map((r) => r.res.Page);
          (off.length ? notify.error : notify.success)(
            `${forms} lead form(s) found${off.length ? `; lead delivery is off for ${off.join(", ")} (use Add connection > Meta lead forms)` : "; leads are delivered to Cloud Office"}`
          );
        }
      } else if (row.kind === "googleLeads") {
        const sources = await workApi.leadSources(params);
        setLeads(sources);
        notify.info(
          sources.google.last_lead_on
            ? `Webhook is receiving leads: ${leadStatusText(sources.google)}`
            : 'Key is set, no leads yet. In Google Ads open the lead form > Lead delivery and press "Send test data".'
        );
      } else {
        const res = row.kind === "whatsapp" ? await workApi.checkWaNumber(row.id) : await workApi.checkConnection(row.id);
        (res.Health === "ok" ? notify.success : notify.error)(
          res.Health === "ok" ? `${row.name || row.platform} is working` : res.Last_Error || `${row.name || row.platform} needs attention`
        );
      }
      load();
    } catch (err) {
      notify.error(errorMessage(err, "Could not test the connection"));
    } finally {
      setBusy("");
    }
  };

  const removeRow = async (row) => {
    if (!(await confirm({ title: "Disconnect", message: `Disconnect ${row.platform}: ${row.name || row.externalId}?` }))) return;
    try {
      if (row.kind === "whatsapp") await workApi.deleteWaNumber(row.id);
      else await workApi.deleteConnection(row.id);
      load();
    } catch (err) {
      notify.error(errorMessage(err, "Could not disconnect"));
    }
  };

  // ---- add actions
  const tokenReady = cfg && form.id.trim() && form.token.trim();

  const testToken = () =>
    run("test", async () => {
      const res = await workApi.testConnection({
        ...(params || {}),
        Platform: option,
        External_Account_Id: form.id.trim(),
        Access_Token: form.token.trim(),
      });
      setResult({ Ok: res.Ok, Text: res.Ok ? `Works${res.Account_Name ? `: ${res.Account_Name}` : ""}` : res.Message });
      if (res.Ok && res.Account_Name && !form.name.trim() && option !== "WhatsApp") {
        setForm((f) => ({ ...f, name: res.Account_Name }));
      }
    });

  const saveToken = () =>
    run("save", async () => {
      if (option === "WhatsApp") {
        await workApi.addWaNumber({
          ...(params || {}),
          Phone_Number_Id: form.id.trim(),
          Waba_Id: form.waba.trim() || undefined,
          Access_Token: form.token.trim(),
        });
      } else {
        await workApi.addConnection({
          ...(params || {}),
          Platform: option,
          External_Account_Id: form.id.trim(),
          Account_Name: form.name.trim() || undefined,
          Access_Token: form.token.trim(),
          Refresh_Token: form.refresh.trim() || undefined,
        });
      }
      notify.success(`${opt.label} connected`);
      setForm(EMPTY_FORM);
      setResult(null);
      setAdding(false);
      load();
    });

  const metaLeadsResult = (res) =>
    res.Ok
      ? {
          Ok: res.Lead_Delivery,
          Text: `${res.Forms.length} lead form(s)${res.Forms.length ? `: ${res.Forms.map((f) => f.name).join(", ")}` : ""} · lead delivery ${res.Lead_Delivery ? "on" : "off"}`,
        }
      : { Ok: false, Text: res.Message };

  const testMetaLeads = () =>
    run("test", async () => setResult(metaLeadsResult(await workApi.testMetaLeadForms(Number(form.page), {}))));

  const saveMetaLeads = () =>
    run("save", async () => {
      const res = await workApi.testMetaLeadForms(Number(form.page), { subscribe: true });
      setResult(metaLeadsResult(res));
      if (res.Ok) {
        notify.success("Meta lead forms connected: new leads arrive in Website Leads");
        load();
      }
    });

  const testGoogleLeads = () =>
    run("test", async () => {
      const sources = await workApi.leadSources(params);
      setLeads(sources);
      const g = sources.google;
      setResult(
        g.has_key || g.vault_key
          ? { Ok: true, Text: g.last_lead_on ? leadStatusText(g) : 'Key is set. Press "Send test data" in the Google Ads lead form.' }
          : { Ok: false, Text: "No webhook key yet. Use Save & keep connected to create one." }
      );
    });

  const saveGoogleLeads = () =>
    run("save", async () => {
      if (
        google?.has_key &&
        !(await confirm({
          title: "New webhook key",
          message: "Lead forms using the old key stop delivering until you paste the new key into them.",
        }))
      ) {
        return;
      }
      const res = await workApi.createGoogleLeadKey(params || {});
      setFreshKey(res.key);
      setResult({ Ok: true, Text: "Key created. Paste the URL and key into each Google Ads lead form, then Send test data." });
      load();
    });

  const signIn = (provider) =>
    run("signin", async () => {
      const res = await workApi.oauthStart(provider, params);
      window.location.assign(res.url);
    });

  const title = (
    <span className="dmp-section__title dms-cat__title">
      <span className="dms-cat__name">
        API Connections
        <span className="dms-svc dms-svc--all">Optional</span>
      </span>
      <span className="dms-cat__desc">Connect now or any time later; it does not count towards setup %.</span>
    </span>
  );

  return (
    <section className={`dmp-section dms-cat dms-conn${isOpen ? " is-open" : ""}`}>
      {collapsible ? (
        <button type="button" className="dmp-section__head dms-cat__head" aria-expanded={isOpen} onClick={onToggle}>
          {title}
          <span className="dmp-section__toggle">
            <FaChevronDown aria-hidden="true" />
          </span>
        </button>
      ) : (
        <div className="dmp-section__head dms-cat__head dms-cat__head--static">{title}</div>
      )}
      {isOpen ? (
        <div className="dms-cx">
          {rows === null ? <p className="dmp-muted">Loading…</p> : null}

          {allRows.length ? (
            <div className="dmp-table-wrap">
              <table className="dms-cx__table">
                <thead>
                  <tr>
                    <th>Platform</th>
                    <th>Account</th>
                    <th>Status</th>
                    <th className="dms-cx__actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allRows.map((row) => {
                    const Icon = PLATFORM_ICONS[row.platform] || FaPlug;
                    const health = HEALTH[row.health] || HEALTH.ok;
                    const removable = row.kind === "social" || row.kind === "whatsapp";
                    return (
                      <tr key={row.key}>
                        <td className="dms-cx__platform">
                          <Icon aria-hidden="true" /> {PLATFORM_LABELS[row.platform] || row.platform}
                        </td>
                        <td>
                          <span className="dms-cx__name">{row.name || row.externalId}</span>
                          <span className="dms-cx__sub">{row.externalId}</span>
                        </td>
                        <td>
                          <span className={`dms-status dms-status--sm dms-status--${health.tone}`}>{health.label}</span>
                          <span className="dms-cx__sub">
                            {row.sub || (row.checkedOn ? `Checked ${fmtShort(row.checkedOn)}` : "Not checked yet")}
                          </span>
                          {row.error ? <span className="dms-cx__sub dms-cx__err">{row.error}</span> : null}
                        </td>
                        <td className="dms-cx__actions-col">
                          <button
                            type="button"
                            className="dmp-btn dmp-btn--ghost dmp-btn--sm"
                            onClick={() => testRow(row)}
                            disabled={!!busy}
                          >
                            <FaSync aria-hidden="true" className={busy === row.key ? "dms-spin" : ""} /> Test connection
                          </button>
                          {removable ? (
                            <button
                              type="button"
                              className="dms-icon-btn"
                              onClick={() => removeRow(row)}
                              title="Disconnect"
                              aria-label={`Disconnect ${row.name || row.platform}`}
                            >
                              <FaTrash aria-hidden="true" />
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}

          {rows !== null && !firstTime ? (
            <button type="button" className="dmp-link-btn dms-cx__add-link" onClick={() => setAdding((v) => !v)}>
              {adding ? <FaTimes aria-hidden="true" /> : <FaPlus aria-hidden="true" />} {adding ? "Close" : "Add connection"}
            </button>
          ) : null}

          {rows !== null && showForm ? (
            <div className="dms-cx__add">
              <div className="dms-cx__add-head">
                <strong>{firstTime ? "Add your first connection" : "Add connection"}</strong>
                {cfg && providers?.[cfg.provider] ? (
                  <button type="button" className="dmp-btn dmp-btn--ghost dmp-btn--sm" onClick={() => signIn(cfg.provider)}>
                    <FaSignInAlt aria-hidden="true" /> Or sign in with {option === "Instagram" ? "Facebook" : option}
                  </button>
                ) : null}
              </div>

              <div className="dms-cx__fields">
                <label className="dmp-field">
                  <span>Connection type</span>
                  <select value={option} onChange={(e) => pickOption(e.target.value)}>
                    {OPTIONS.map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>

                {opt.kind === "token" ? (
                  <>
                    <label className="dmp-field" title={cfg.idHint}>
                      <span>
                        {cfg.idLabel} <span className="dms-req">*</span>
                      </span>
                      <input value={form.id} onChange={set("id")} placeholder={cfg.idHint} />
                    </label>
                    {cfg.wabaLabel ? (
                      <label className="dmp-field">
                        <span>{cfg.wabaLabel}</span>
                        <input value={form.waba} onChange={set("waba")} />
                      </label>
                    ) : (
                      <label className="dmp-field">
                        <span>{cfg.nameLabel}</span>
                        <input value={form.name} onChange={set("name")} placeholder="Filled in by Test connection" />
                      </label>
                    )}
                    <label className="dmp-field" title={cfg.tokenHint}>
                      <span>
                        {cfg.tokenLabel} <span className="dms-req">*</span>
                      </span>
                      <input type="password" autoComplete="off" value={form.token} onChange={set("token")} placeholder={cfg.tokenHint} />
                    </label>
                    {cfg.refresh ? (
                      <label className="dmp-field">
                        <span>Refresh token</span>
                        <input type="password" autoComplete="off" value={form.refresh} onChange={set("refresh")} placeholder="Keeps it connected" />
                      </label>
                    ) : null}
                  </>
                ) : null}

                {opt.kind === "metaLeads" ? (
                  fbPages.length ? (
                    <label className="dmp-field">
                      <span>
                        Facebook page <span className="dms-req">*</span>
                      </span>
                      <select value={form.page} onChange={set("page")}>
                        <option value="">Pick the page that runs the lead ads</option>
                        {fbPages.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : (
                    <p className="dms-cx__note">
                      Lead forms belong to a Facebook page. Connect the page first (Connection type: Facebook Page), then come back here.
                    </p>
                  )
                ) : null}

                {opt.kind === "googleLeads" ? (
                  <div className="dms-cx__note">
                    In Google Ads open each lead form &gt; Lead delivery &gt; Webhook, paste the URL and key, then press
                    &quot;Send test data&quot;. New leads land in Website Leads with an email alert.
                    <CopyValue label="Webhook URL" value={google?.url} />
                    {freshKey ? <CopyValue label="Key (shown only now)" value={freshKey} /> : null}
                    {!freshKey && google?.has_key ? <div className="dmp-muted">A key is set (ends {google.key_hint}).</div> : null}
                    {!freshKey && !google?.has_key && google?.vault_key ? (
                      <div className="dmp-muted">Using the Lead Form Webhook Key from the client&apos;s Vault (Google Ads app).</div>
                    ) : null}
                  </div>
                ) : null}

                {opt.kind === "signin" ? (
                  <div className="dms-cx__note">
                    {opt.label}s are connected by signing in with the business account that runs the ads.
                    {providers?.[opt.provider] ? (
                      <div>
                        <button type="button" className="dmp-btn dmp-btn--sm" onClick={() => signIn(opt.provider)} disabled={!!busy}>
                          <FaSignInAlt aria-hidden="true" /> Sign in with {opt.signInLabel}
                        </button>
                      </div>
                    ) : (
                      <div className="dmp-muted">Not set up yet: add the client&apos;s own app in Vault, or ask Tumma to enable it.</div>
                    )}
                  </div>
                ) : null}
              </div>

              {opt.kind !== "signin" ? (
                <div className="dms-cx__actions">
                  <ResultLine result={result} />
                  <button
                    type="button"
                    className="dmp-btn dmp-btn--ghost dmp-btn--sm"
                    disabled={
                      !!busy ||
                      (opt.kind === "token" && !tokenReady) ||
                      (opt.kind === "metaLeads" && !form.page)
                    }
                    onClick={opt.kind === "token" ? testToken : opt.kind === "metaLeads" ? testMetaLeads : testGoogleLeads}
                  >
                    <FaSync aria-hidden="true" className={busy === "test" ? "dms-spin" : ""} /> Test connection
                  </button>
                  <button
                    type="button"
                    className="dmp-btn dmp-btn--sm"
                    disabled={
                      !!busy ||
                      (opt.kind === "token" && !tokenReady) ||
                      (opt.kind === "metaLeads" && !form.page)
                    }
                    onClick={opt.kind === "token" ? saveToken : opt.kind === "metaLeads" ? saveMetaLeads : saveGoogleLeads}
                  >
                    <FaSave aria-hidden="true" />{" "}
                    {busy === "save"
                      ? "Saving…"
                      : opt.kind === "googleLeads" && google?.has_key
                        ? "Create a new key"
                        : "Save & keep connected"}
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

export default SetupConnections;
